/**
 * Readiness header / snapshot — 5-way readiness, NO aggregate score.
 * Plan §26.9.
 *
 * Kaiyang Lesson §25.7 / ADR-0002 red line #3 enforced structurally:
 *   1. Five SIBLING required fields: legal / evidenceStandard / marketForce /
 *      ecosystem / continuingObligation. No `overallReadinessPercent`, no
 *      `score`, no `readinessPercent`. CI red line #3 grep-rejects those names.
 *   2. `marketForce.nonLegalNotice` and `ecosystem.nonLegalNotice` are locked
 *      to the exact literal `"Excluded from Legal Readiness"`.
 *   3. `legal.activeBindingPercent` is a separate field — never share denominator
 *      with `legal.percent`, which mixes DRAFT planning items.
 *   4. Citation depth has its own block; the active-binding count + the
 *      article-or-deeper percentage are sibling honest signals, not a derived
 *      headline.
 */

import { z } from "zod";
import { CitationGranularity } from "./primitives";

const Pct = z.number().int().min(0).max(100);
const Count = z.number().int().nonnegative();

const ReadinessLegalSchema = z.object({
  percent: Pct,
  activeBindingPercent: Pct,
  applicableHardLawCount: Count,
  activeBindingHardLawCount: Count,
  draftMonitoringCount: Count,
  placeholderCurationGapCount: Count,
  hiddenByConfigCount: Count,
  unknownNeedsConfigCount: Count,
  citationDepth: z.object({
    hardLawArticleOrDeeperPercent: Pct,
    regulationOnlyGapCardCount: Count,
    hardLawReferencesByGranularity: z.record(CitationGranularity, Count),
  }),
});

const ReadinessNonLegalSchema = z.object({
  percent: Pct,
  applicableCount: Count,
  nonLegalNotice: z.literal("Excluded from Legal Readiness"),
});

export const ReadinessHeaderSchema = z.object({
  legal: ReadinessLegalSchema,
  evidenceStandard: z.object({ percent: Pct, applicableCount: Count }),
  marketForce: ReadinessNonLegalSchema,
  ecosystem: ReadinessNonLegalSchema,
  continuingObligation: z.object({
    percent: Pct,
    coverageCount: Count,
    byTiming: z.record(
      z.enum(["post_market", "recurring", "event_triggered", "phase_in"]),
      Count,
    ),
  }),
});
export type ReadinessHeader = z.infer<typeof ReadinessHeaderSchema>;

export const CoverageReadinessSchema = z.object({
  totalDomainPacks: Count,
  implementedAnchorCount: Count,
  sourceGapCount: Count,
  highHardLawBlockingGapCount: Count,
  totalSubmissionBlockingGapCount: Count,
  distinctBlockerCount: Count,
  submissionBlockingGaps: z.array(
    z.object({
      domain: z.string(),
      vehicleScope: z.string(),
      pattern: z.string(),
      reason: z.string(),
      targetDate: z.string(),
      /**
       * NOTE: `owner` here is a SOURCE-SIDE field carried over from
       * the registry's coverage doc. It is NOT an OEM dispatch target —
       * `regpulse-core` schemas never wire owners onto cards or actions.
       * See ADR-0002 red line #5 (no card-level owner/assignee).
       */
      owner: z.string(),
    }),
  ),
  canClaimCompleteCoverage: z.boolean(),
  hasNoHighHardLawBlockers: z.boolean(),
  hasNoUnacknowledgedSourceGaps: z.boolean(),
  demoScope: z.object({
    scopeLabel: z.literal("EU/UNECE/FR/DE/ES/UK"),
    sourceGapCount: Count,
    p0SourceGapCount: Count,
    canClaimDemoScopeP0Coverage: z.boolean(),
    remainingPatterns: z.array(z.string()),
  }),
});
export type CoverageReadiness = z.infer<typeof CoverageReadinessSchema>;

export const ReadinessSnapshotSchema = z.object({
  snapshotId: z.string().min(1),
  registrySnapshotId: z.string().min(1),
  registrySnapshotDate: z.string(),
  generatedAt: z.string(),
  vehicleProfileId: z.string().min(1),
  configCompletenessPercent: Pct,
  header: ReadinessHeaderSchema,
  coverageReadiness: CoverageReadinessSchema,
  cardsByObligationKind: z.record(z.string(), Count),
  cardsByObligationTiming: z.record(z.string(), Count),
  cardsByRegulatoryDomain: z.record(z.string(), Count),
  hiddenByRegulatoryDomain: z.record(z.string(), Count),
  unknownByRegulatoryDomain: z.record(z.string(), Count),
});
export type ReadinessSnapshot = z.infer<typeof ReadinessSnapshotSchema>;
